import { z } from "zod";
import { appIdSchema, isoTimestampSchema, opaqueRefSchema, } from "./identifiers.js";
import { agentCommandSchema } from "./runs.js";
import { continuityCheckpointRequestSchema, continuityDigestSchema, continuityIdentitySchema, continuityObjectReceiptSchema, runtimeContinuityBindingSchema, workspaceStorageSchema, } from "./continuity.js";
import { agentAppMutationJournalGenerationSchema, runtimeStartSchema, } from "./runtime.js";
export const MAX_RUNTIME_LAUNCH_REQUEST_BYTES = 2 * 1024 * 1024;
export const runtimeLaunchRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    start: runtimeStartSchema,
    command: agentCommandSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.start.continuity) !== Boolean(value.command.continuity)) {
        context.addIssue({ code: "custom", path: ["command", "continuity"], message: "Launch continuity must match the admitted command capability" });
    }
    if (value.start.runId !== value.command.runId) {
        context.addIssue({
            code: "custom",
            path: ["command", "runId"],
            message: "RuntimeStart and AgentCommand must target the same run",
        });
    }
    if (value.start.kind !== "root" || value.start.parentRunId !== null) {
        context.addIssue({
            code: "custom",
            path: ["start", "kind"],
            message: "The launcher creates root runtimes only; child agents stay in-process",
        });
    }
    if (value.command.type === "run.pause" || value.command.type === "run.cancel") {
        context.addIssue({
            code: "custom",
            path: ["command", "type"],
            message: "A runtime launch requires a start, continuation, or answer command",
        });
    }
    if (value.start.authorityMode === "app_owner_v1" &&
        value.start.mutationJournalState !== "ready") {
        context.addIssue({
            code: "custom",
            path: ["start", "mutationJournalState"],
            message: "An app-owner runtime launch requires a ready mutation journal",
        });
    }
});
export const runtimeMutationJournalRetireRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    appId: appIdSchema,
    generation: agentAppMutationJournalGenerationSchema,
    state: z.literal("retiring"),
})
    .strict();
export const runtimeMutationJournalRetireResultSchema = z
    .object({
    schemaVersion: z.literal(1),
    appId: appIdSchema,
    generation: agentAppMutationJournalGenerationSchema,
    state: z.literal("retired"),
    removed: z.boolean(),
})
    .strict();
export const runtimeLifecycleStateSchema = z.enum([
    "provisioning",
    "starting",
    "running",
    "stopping",
    "exited",
    "failed",
    "cancelled",
    "removed",
]);
export const runtimeFailureClassSchema = z.enum([
    "image_unavailable",
    "startup_timeout",
    "startup_failed",
    "callback_unavailable",
    "storage_full",
    "out_of_memory",
    "engine_failed",
    "forced_stop",
    "unexpected_exit",
    "capacity_exceeded",
    "invalid_start",
]);
export const runtimeAttemptSchema = z.number().int().positive();
export const runtimeHandleSchema = z
    .object({
    schemaVersion: z.literal(1),
    runtimeId: opaqueRefSchema,
    runId: z.uuid(),
    // Legacy runtimes created before attempt fencing are observable as null so
    // a controlled drain can reconcile them. Every new launch returns a
    // positive attempt, and all mutating requests require one.
    runtimeAttempt: runtimeAttemptSchema.nullable(),
    appId: z.uuid(),
    userId: z.uuid(),
    containerName: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[a-zA-Z0-9][a-zA-Z0-9_.-]+$/),
    state: runtimeLifecycleStateSchema,
    failureClass: runtimeFailureClassSchema.nullable(),
    retryable: z.boolean(),
    containerState: z.string().min(1).max(100).nullable(),
    exitCode: z.number().int().nullable(),
    oomKilled: z.boolean(),
    createdAt: isoTimestampSchema,
    startedAt: isoTimestampSchema.nullable(),
    finishedAt: isoTimestampSchema.nullable(),
})
    .strict();
export const runtimeControlRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    runtimeAttempt: runtimeAttemptSchema,
    reason: z.string().trim().min(1).max(500),
    graceMilliseconds: z.number().int().min(0).max(30_000).default(5_000),
})
    .strict();
export const runtimeCommandRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    runtimeAttempt: runtimeAttemptSchema,
    command: agentCommandSchema,
})
    .strict();
export const runtimeCleanupRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    runtimeAttempt: runtimeAttemptSchema,
    continuity: continuityIdentitySchema.optional(),
    preserveContinuation: z.boolean().optional(),
})
    .strict();
export const runtimeContinuitySnapshotRequestSchema = z.object({
    schemaVersion: z.literal(1),
    runtimeAttempt: runtimeAttemptSchema,
    continuity: runtimeContinuityBindingSchema,
    checkpoint: continuityCheckpointRequestSchema,
}).strict();
export const runtimeContinuitySnapshotResultSchema = z.object({
    schemaVersion: z.literal(1),
    manifest: continuityObjectReceiptSchema.refine((value) => value.kind === "manifest", "Snapshot requires a manifest receipt"),
}).strict();
export const runtimeWorkspaceSnapshotRequestSchema = z.object({
    schemaVersion: z.literal(1), runtimeAttempt: runtimeAttemptSchema, workspace: workspaceStorageSchema,
}).strict();
export const runtimeContinuityRestoreRequestSchema = z.object({
    schemaVersion: z.literal(1),
    runtimeAttempt: runtimeAttemptSchema,
    continuity: runtimeContinuityBindingSchema,
}).strict().refine((value) => value.continuity.checkpoint !== null, "Restore requires an immutable checkpoint");
export const runtimeContinuityRestoreResultSchema = z.object({
    schemaVersion: z.literal(1),
    restored: z.boolean(),
    digest: continuityDigestSchema,
}).strict();
export const runtimeContinuityRetireRequestSchema = z.object({
    schemaVersion: z.literal(1),
    userId: z.uuid(),
    appId: z.uuid(),
    continuity: continuityIdentitySchema,
    state: z.literal("retiring"),
}).strict();
export const runtimeContinuityRetireResultSchema = z.object({
    schemaVersion: z.literal(1),
    continuity: continuityIdentitySchema,
    state: z.literal("retired"),
    removed: z.boolean(),
}).strict();
export const runtimeWorkspaceRetireRequestSchema = z.object({
    schemaVersion: z.literal(1),
    userId: z.uuid(),
    appId: z.uuid(),
    workspaceGeneration: z.uuid(),
    state: z.literal("retiring"),
}).strict();
export const runtimeWorkspaceRetireResultSchema = z.object({
    schemaVersion: z.literal(1),
    userId: z.uuid(),
    appId: z.uuid(),
    workspaceGeneration: z.uuid(),
    state: z.literal("retired"),
    removed: z.boolean(),
}).strict();
//# sourceMappingURL=launcher.js.map